// src/app/views/prueba-informativa/dialog-form-prueba-informativa/dialog-form-prueba-informativa.component.ts
import { ChangeDetectionStrategy, Component, Inject, Optional, OnInit, ChangeDetectorRef, Input } from '@angular/core';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA, MatDialogModule } from '@angular/material/dialog';
import { CommonModule } from '@angular/common';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatOptionModule } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatNativeDateModule } from '@angular/material/core';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSliderModule } from '@angular/material/slider';
import { MatTableModule } from '@angular/material/table';
import { MatTabsModule } from '@angular/material/tabs';
import { NgxSpinnerModule, NgxSpinnerService } from 'ngx-spinner';

import { AccionesDialog } from '@models/base/identificadores.model';
import { ExpedienteService } from '@services/expediente/expediente.service';

import { ButtonCancelDialogComponent } from '@component/button-cancel-dialog/button-cancel-dialog.component';
import { ButtonCloseDialogComponent } from '@component/button-close-dialog/button-close-dialog.component';
import { ButtonAddComponent } from '@component/button-add/button-add.component';
import { ButtonSaveComponent } from '@component/button-save/button-save.component';
import { ButtonDeleteComponent } from '@component/button-delete/button-delete.component';
import { PersonaDetailDto } from '@models/persona/personaDetailDto.model';
import { CreatePruebaInformativaRequest } from '@models/pruebas/pruebaInformativa/createPruebaInformativaRequest.model';
import { PruebaInformativaDto } from '@models/pruebas/pruebaInformativa/pruebaInformativaDto.model';
import { UpdatePruebaInformativaRequest } from '@models/pruebas/pruebaInformativa/updatePruebaInformativaRequest.model';
import { EstadoPruebaService } from '@services/estadoPrueba/estado-prueba.service';
import { SnackBarService } from '@services/snackBar/snack-bar-service.service';
import { TipoIntervinienteService } from '@services/tipoInterviniente/tipoInterviniente.service';
import { UsuarioService } from '@services/usuario/usuario.service';
import { BuscarPersonaComponent } from '@views/busquedas/buscar-persona/buscar-persona.component';
import { PruebaInformativaService } from '@services/pruebas/pruebaInformativa/pruebaInformativa.service';

@Component({
  selector: 'app-dialog-form-prueba-informativa',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatCardModule,
    MatIconModule,
    MatButtonModule,
    MatDividerModule,
    NgxSpinnerModule,
    MatTableModule,
    MatSliderModule,
    MatSlideToggleModule,
    MatMenuModule,
    MatDialogModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatExpansionModule,
    MatSelectModule,
    MatOptionModule,
    MatTabsModule,
    ButtonCancelDialogComponent,
    ButtonCloseDialogComponent,
    ButtonAddComponent,
    ButtonSaveComponent,
    ButtonDeleteComponent,
    BuscarPersonaComponent
  ],
  templateUrl: './dialog-form-prueba-informativa.component.html',
  styleUrl: './dialog-form-prueba-informativa.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DialogFormPruebaInformativaComponent implements OnInit {
  @Input() idExpediente: any;
  formBuilder = new FormBuilder();
  pruebaInformativaDto!: PruebaInformativaDto;
  usuarios: any[] = [];
  tiposIntervinientes: any[] = [];
  estadosPrueba: any[] = [];
  expedientes: any[] = [];

  // Persona informante
  personaInformanteSeleccionada: PersonaDetailDto | null = null;
  expedienteSeleccionado: any;

  // Getter para acceder a los controles del formulario fácilmente
  get f() { return this.form.controls; }

  accionesDialog = AccionesDialog;
  form!: FormGroup;
  localData: any;

  // Para el template - asegurar que PersonaDetailDto esté disponible
  PersonaDetailDto = PersonaDetailDto;

  constructor(
    public dialogRef: MatDialogRef<DialogFormPruebaInformativaComponent>,
    private snackBar: SnackBarService,
    private spinner: NgxSpinnerService,
    private pruebaInformativaService: PruebaInformativaService,
    private usuarioService: UsuarioService,
    private tipoIntervinienteService: TipoIntervinienteService,
    private estadoPruebaService: EstadoPruebaService,
    private expedienteService: ExpedienteService,
    private cdr: ChangeDetectorRef,
    @Optional() @Inject(MAT_DIALOG_DATA) public data: any
  ) {
    this.localData = { ...data };
    this.initializeForm();
  }

  ngOnInit(): void {
    this.loadCatalogs();
    this.setFormData();
  }

  initializeForm() {
    this.form = this.formBuilder.group({
      Descripcion: ["", [Validators.required, Validators.maxLength(500)]],
      Observaciones: ["", [Validators.maxLength(1000)]],
      Domicilio: ["", [Validators.maxLength(300)]],
      InformaSobre: ["", [Validators.required, Validators.maxLength(1000)]],
      IdUsuarioResponsable: ["", [Validators.required]],
      IdTipoInterviniente: [""],
      IdEstadoPrueba: [""],
      FechaOficioConfronte: [""],
      FechaOficioRetirado: [""],
      FechaOficioDiligenciado: [""],
      FechaOficioContestado: [""],
      FechaVenceContestacion: [""],
      IdExpediente: [""]
    });
  }

  setFormData() {
    if (this.data?.accion === AccionesDialog.Modificar && this.data?.id) {
      this.form.patchValue({
        Descripcion: this.data.descripcion || '',
        Observaciones: this.data.observaciones || '',
        Domicilio: this.data.domicilio || '',
        InformaSobre: this.data.informaSobre || '',
        IdUsuarioResponsable: this.data.usuarioResponsable?.id || '',
        IdTipoInterviniente: this.data.tipoInterviniente?.id || '',
        IdEstadoPrueba: this.data.estadoPrueba?.id || '',
        FechaOficioConfronte: this.data.fechaOficioConfronte || null,
        FechaOficioRetirado: this.data.fechaOficioRetirado || null,
        FechaOficioDiligenciado: this.data.fechaOficioDiligenciado || null,
        FechaOficioContestado: this.data.fechaOficioContestado || null,
        FechaVenceContestacion: this.data.fechaVenceContestacion || null,
        IdExpediente: this.data.idExpediente || this.idExpediente || ''
      });

      // Cargar persona informante si existe
      if (this.data.personaInformante) {
        this.personaInformanteSeleccionada = new PersonaDetailDto();
        this.personaInformanteSeleccionada.id = this.data.personaInformante.id;
        this.personaInformanteSeleccionada.nombreCompleto = this.data.personaInformante.nombreCompleto;
      }
    }

    if (this.data?.accion === AccionesDialog.Agregar && this.data?.idExpediente) {
      this.form.patchValue({
        IdExpediente: this.data.idExpediente || this.idExpediente || ''
      });
    }

    // Marcar para detección de cambios
    this.cdr.markForCheck();
  }

  loadCatalogs() {
    this.spinner.show('spBusquedaUsuarios');

    // Cargar usuarios
    this.usuarioService.getAll().subscribe({
      next: (usuarios) => {
        this.usuarios = usuarios;
        this.spinner.hide('spBusquedaUsuarios');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaUsuarios');
        this.snackBar.showError(error, 'Error al cargar usuarios');
        this.cdr.markForCheck();
      }
    });

    this.spinner.show('spBusquedaTiposIntervinientes');

    // Cargar tipos de interviniente
    this.tipoIntervinienteService.getAll().subscribe({
      next: (tipos) => {
        this.tiposIntervinientes = tipos;
        this.spinner.hide('spBusquedaTiposIntervinientes');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaTiposIntervinientes');
        this.snackBar.showError(error, 'Error al cargar tipos de interviniente');
        this.cdr.markForCheck();
      }
    });

    this.spinner.show('spBusquedaEstados');

    // Cargar estados de prueba
    this.estadoPruebaService.getAll().subscribe({
      next: (estados) => {
        this.estadosPrueba = estados;
        this.spinner.hide('spBusquedaEstados');
        this.cdr.markForCheck();
      },
      error: (error) => {
        this.spinner.hide('spBusquedaEstados');
        this.snackBar.showError(error, 'Error al cargar estados de prueba');
        this.cdr.markForCheck();
      }
    });
  }

  onPersonaInformanteSelected(persona: PersonaDetailDto): void {
    this.personaInformanteSeleccionada = persona;
    this.cdr.markForCheck();
  }

  agregar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const createRequest: CreatePruebaInformativaRequest = new CreatePruebaInformativaRequest();
    this.mapFormToCreateRequest(createRequest);
    this.create(createRequest);
  }

  modificar() {
    if (!this.form.valid) {
      this.markFormGroupTouched();
      return;
    }

    const updateRequest: UpdatePruebaInformativaRequest = new UpdatePruebaInformativaRequest();
    updateRequest.id = this.data.id;
    this.mapFormToUpdateRequest(updateRequest);
    this.update(updateRequest);
  }

  eliminar() {
    if (!this.data?.id) {
      this.snackBar.showError("No se puede eliminar: ID no válido", "Error");
      return;
    }

    this.delete(this.data.id);
  }

  private mapFormToCreateRequest(request: CreatePruebaInformativaRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.domicilio = this.form.get("Domicilio")?.value || '';
    request.informaSobre = this.form.get("InformaSobre")?.value || '';
    request.idUsuarioResponsable = this.form.get("IdUsuarioResponsable")?.value || null;
    if(this.personaInformanteSeleccionada?.id)
    request.idPersonaInformante = this.personaInformanteSeleccionada?.id;
    request.idTipoInterviniente = this.form.get("IdTipoInterviniente")?.value || null;
    request.idEstadoPrueba = this.form.get("IdEstadoPrueba")?.value || null;
    request.fechaOficioConfronte = this.form.get("FechaOficioConfronte")?.value || null;
    request.fechaOficioRetirado = this.form.get("FechaOficioRetirado")?.value || null;
    request.fechaOficioDiligenciado = this.form.get("FechaOficioDiligenciado")?.value || null;
    request.fechaOficioContestado = this.form.get("FechaOficioContestado")?.value || null;
    request.fechaVenceContestacion = this.form.get("FechaVenceContestacion")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
  }

  private mapFormToUpdateRequest(request: UpdatePruebaInformativaRequest) {
    request.descripcion = this.form.get("Descripcion")?.value || '';
    request.observaciones = this.form.get("Observaciones")?.value || '';
    request.domicilio = this.form.get("Domicilio")?.value || '';
    request.informaSobre = this.form.get("InformaSobre")?.value || '';
    request.idUsuarioResponsable = this.form.get("IdUsuarioResponsable")?.value || null;
    if(this.personaInformanteSeleccionada?.id)
    request.idPersonaInformante = this.personaInformanteSeleccionada?.id ;
    request.idTipoInterviniente = this.form.get("IdTipoInterviniente")?.value || null;
    request.idEstadoPrueba = this.form.get("IdEstadoPrueba")?.value || null;
    request.fechaOficioConfronte = this.form.get("FechaOficioConfronte")?.value || null;
    request.fechaOficioRetirado = this.form.get("FechaOficioRetirado")?.value || null;
    request.fechaOficioDiligenciado = this.form.get("FechaOficioDiligenciado")?.value || null;
    request.fechaOficioContestado = this.form.get("FechaOficioContestado")?.value || null;
    request.fechaVenceContestacion = this.form.get("FechaVenceContestacion")?.value || null;
    request.idExpediente = this.form.get("IdExpediente")?.value || null;
  }

  onExpedienteSelected(expediente: any): void {
    this.expedienteSeleccionado = expediente;
    if (expediente?.id) {
      this.form.get('IdExpediente')?.setValue(expediente.id);
    }
    this.cdr.markForCheck();
  }

  closeDialog() {
    this.dialogRef.close({ event: AccionesDialog.Cancelar });
  }

  create(createRequest: CreatePruebaInformativaRequest) {
    this.spinner.show("spCrearPruebaInformativa");
    this.pruebaInformativaService.create(createRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spCrearPruebaInformativa");
          this.snackBar.showSuccess("Prueba Informativa creada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Agregar, data });
        },
        error: (error) => {
          this.spinner.hide("spCrearPruebaInformativa");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  update(updateRequest: UpdatePruebaInformativaRequest) {
    this.spinner.show("spModificarPruebaInformativa");
    this.pruebaInformativaService.update(updateRequest)
      .subscribe({
        next: (data) => {
          this.spinner.hide("spModificarPruebaInformativa");
          this.snackBar.showSuccess("Prueba Informativa modificada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Modificar, data });
        },
        error: (error) => {
          this.spinner.hide("spModificarPruebaInformativa");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  delete(id: number) {
    this.spinner.show("spEliminarPruebaInformativa");
    this.pruebaInformativaService.delete(id)
      .subscribe({
        next: () => {
          this.spinner.hide("spEliminarPruebaInformativa");
          this.snackBar.showSuccess("Prueba Informativa eliminada correctamente.", "Éxito");
          this.dialogRef.close({ event: AccionesDialog.Eliminar });
        },
        error: (error) => {
          this.spinner.hide("spEliminarPruebaInformativa");
          this.snackBar.showError(error, "Error");
        }
      });
  }

  private markFormGroupTouched() {
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key)?.markAsTouched();
    });
    this.cdr.markForCheck();
  }

  trackByItems(index: number, item: any): any {
    return item?.id || index;
  }
}
